import { useState, useEffect, useCallback } from 'react';
import { SMContact, BankerDocument } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import {
  subscribeToBankers,
  addBanker,
  updateBanker,
  deleteBanker,
  uploadBankerDocument,
  deleteBankerDocument,
  toggleBankerFavorite,
  subscribeToFavoriteBankers,
  checkBankerExists,
} from '@/services/bankerService';

export function useBankers() {
  const { user } = useAuth();
  const [bankers, setBankers] = useState<SMContact[]>([]);
  const [favoriteBankerIds, setFavoriteBankerIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Subscribe to all bankers (shared across users)
  useEffect(() => {
    setLoading(true);
    setError(null);

    const unsubscribe = subscribeToBankers((updatedBankers) => {
      setBankers(updatedBankers);
      setLoading(false);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // Subscribe to user's favorite bankers
  useEffect(() => {
    if (!user?.id) {
      setFavoriteBankerIds([]);
      return;
    }

    const unsubscribe = subscribeToFavoriteBankers(user.id, (bankerIds) => {
      setFavoriteBankerIds(bankerIds);
    });

    return () => {
      unsubscribe();
    };
  }, [user?.id]);

  // Add a new banker
  const addNewBanker = useCallback(
    async (banker: Omit<SMContact, 'id'>): Promise<SMContact> => {
      if (!user?.id) {
        throw new Error('User must be authenticated to add a banker');
      }
      
      const digits = (banker.phone || "").replace(/\D/g, "");
      if (digits.length < 10) {
        throw new Error("Phone number must contain at least 10 digits");
      }

      const bankerWithUser = {
        ...banker,
        createdBy: user.id,
        phone: `+91${digits.slice(-10)}`,
      };
      const newBanker = await addBanker(bankerWithUser);
      return newBanker;
    },
    [user?.id]
  );

  // Update a banker
  const updateExistingBanker = useCallback(
    async (bankerId: string, updates: Partial<SMContact>): Promise<void> => {
      if (!user?.id) {
        throw new Error("User must be authenticated to update a banker");
      }

      let cleanedUpdates: Partial<SMContact> = updates;
      if (updates.phone !== undefined) {
        const digits = (updates.phone || "").replace(/\D/g, "");
        if (digits.length < 10) {
          throw new Error("Phone number must contain at least 10 digits");
        }
        cleanedUpdates = { ...updates, phone: `+91${digits.slice(-10)}` };
      }

      await updateBanker(bankerId, cleanedUpdates);
    },
    [user?.id],
  );

  // Delete a banker
  const deleteBankerContact = useCallback(
    async (bankerId: string): Promise<void> => {
      if (!user?.id) {
        throw new Error('User must be authenticated to delete a banker');
      }

      // Check if user created this banker
      const banker = bankers.find(b => b.id === bankerId);
      if (banker && banker.createdBy !== user.id) {
        throw new Error('You can only delete bankers you created');
      }

      await deleteBanker(bankerId);
    },
    [user?.id, bankers]
  );

  // Upload a document
  const uploadDocument = useCallback(
    async (file: File, bankerId: string): Promise<BankerDocument> => {
      if (!user?.id) {
        throw new Error('User must be authenticated to upload documents');
      }

      const document = await uploadBankerDocument(file, bankerId);
      return document;
    },
    [user?.id]
  );

  // Delete a document
  const deleteDocument = useCallback(
    async (fileUrl: string): Promise<void> => {
      if (!user?.id) {
        throw new Error('User must be authenticated to delete documents');
      }

      await deleteBankerDocument(fileUrl);
    },
    [user?.id]
  );

  // Toggle favorite
  const toggleFavorite = useCallback(
    async (bankerId: string): Promise<boolean> => {
      if (!user?.id) {
        throw new Error('User must be authenticated to favorite bankers');
      }

      const isFavorited = await toggleBankerFavorite(user.id, bankerId);
      return isFavorited;
    },
    [user?.id]
  );

  // Check if banker exists
  const checkExists = useCallback(
    async (phone: string, excludeId?: string): Promise<boolean> => {
      return await checkBankerExists(phone, excludeId);
    },
    []
  );

  // Get a specific banker
  const getBanker = useCallback(
    (bankerId: string): SMContact | undefined => {
      return bankers.find(b => b.id === bankerId);
    },
    [bankers]
  );

  // Check if banker is favorited by current user
  const isFavorite = useCallback(
    (bankerId: string): boolean => {
      return favoriteBankerIds.includes(bankerId);
    },
    [favoriteBankerIds]
  );

  // Check if current user can edit a banker
  // Any authenticated non-member user can edit any banker contact (aligns with Firestore rules)
  const canEdit = useCallback(
    (_bankerId: string): boolean => {
      return !!user?.id && user.primaryRole !== 'member';
    },
    [user?.id, user?.primaryRole],
  );

  // Report a banker — allowed for any authenticated user (no ownership check)
  const reportBanker = useCallback(
    async (bankerId: string, reason: string): Promise<void> => {
      if (!user?.id) {
        throw new Error('User must be authenticated to report a banker');
      }
      await updateBanker(bankerId, {
        isReported: true,
        reportReason: reason,
      });
    },
    [user?.id]
  );

  return {
    bankers,
    loading,
    error,
    addBanker: addNewBanker,
    updateBanker: updateExistingBanker,
    deleteBanker: deleteBankerContact,
    uploadDocument,
    deleteDocument,
    toggleFavorite,
    checkExists,
    getBanker,
    isFavorite,
    canEdit,
    reportBanker,
  };
}
